import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Playlist, PlaylistSong } from '@/types/playlist';
import { playlistStorage } from '@/services/storage/playlists';
import { Song, SongRevision } from '@/types/song';

interface PlaylistContextType {
  playlists: Playlist[];
  loading: boolean;
  getPlaylist: (id: string) => Promise<Playlist | undefined>;
  createPlaylist: (name: string, description?: string) => Promise<void>;
  deletePlaylist: (id: string) => Promise<void>;
  addTracksToPlaylist: (playlistId: string, tracks: Array<{ songId: number; songName: string; coverArtUrl?: string; revisionId?: number; revisionName?: string }>) => Promise<void>;
  removeTrackFromPlaylist: (playlistId: string, trackIndex: number) => Promise<void>;
  addSongToPlaylist: (playlistId: string, song: Song, revision?: SongRevision) => Promise<void>;
  removeSongFromPlaylist: (playlistId: string, songId: number) => Promise<void>;
  reorderSongs: (playlistId: string, songs: PlaylistSong[]) => Promise<void>;
  exportPlaylists: () => Promise<string>;
  importPlaylists: (jsonData: string) => Promise<void>;
}

const PlaylistContext = createContext<PlaylistContextType | undefined>(undefined);

export function PlaylistProvider({ children }: { children: ReactNode }) {
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPlaylists();
  }, []);

  async function loadPlaylists() {
    try {
      const data = await playlistStorage.getAllPlaylists();
      setPlaylists(data);
    } catch (error) {
      console.error('Failed to load playlists:', error);
    } finally {
      setLoading(false);
    }
  }

  async function getPlaylist(id: string) {
    return await playlistStorage.getPlaylist(id);
  }

  async function addTracksToPlaylist(
    playlistId: string,
    tracks: Array<{ songId: number; songName: string; coverArtUrl?: string; revisionId?: number; revisionName?: string }>
  ) {
    await playlistStorage.addTracksToPlaylist(playlistId, tracks);
    await loadPlaylists();
  }

  async function removeTrackFromPlaylist(playlistId: string, trackIndex: number) {
    await playlistStorage.removeTrackFromPlaylist(playlistId, trackIndex);
    await loadPlaylists();
  }

  async function createPlaylist(name: string, description?: string) {
    const newPlaylist: Playlist = {
      id: crypto.randomUUID(),
      name,
      description,
      songs: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    await playlistStorage.createPlaylist(newPlaylist);
    await loadPlaylists();
  }

  async function deletePlaylist(id: string) {
    await playlistStorage.deletePlaylist(id);
    await loadPlaylists();
  }

  async function addSongToPlaylist(playlistId: string, song: Song, revision?: SongRevision) {
    const playlist = await playlistStorage.getPlaylist(playlistId);
    if (!playlist) return;

    const newSong: PlaylistSong = {
      songId: song.songId,
      songName: song.songName,
      coverArtUrl: song.coverArtUrl,
      revisionId: revision?.songRevisionId,
      addedAt: new Date(),
      order: playlist.songs.length,
    };

    playlist.songs.push(newSong);
    await playlistStorage.updatePlaylist(playlistId, { songs: playlist.songs });
    await loadPlaylists();
  }

  async function removeSongFromPlaylist(playlistId: string, songId: number) {
    const playlist = await playlistStorage.getPlaylist(playlistId);
    if (!playlist) return;

    playlist.songs = playlist.songs.filter((s) => s.songId !== songId);
    await playlistStorage.updatePlaylist(playlistId, { songs: playlist.songs });
    await loadPlaylists();
  }

  async function reorderSongs(playlistId: string, songs: PlaylistSong[]) {
    await playlistStorage.updatePlaylist(playlistId, { songs });
    await loadPlaylists();
  }

  async function exportPlaylists() {
    return await playlistStorage.exportPlaylists();
  }

  async function importPlaylists(jsonData: string) {
    await playlistStorage.importPlaylists(jsonData);
    await loadPlaylists();
  }

  const value = {
    playlists,
    loading,
    getPlaylist,
    createPlaylist,
    deletePlaylist,
    addTracksToPlaylist,
    removeTrackFromPlaylist,
    addSongToPlaylist,
    removeSongFromPlaylist,
    reorderSongs,
    exportPlaylists,
    importPlaylists,
  };

  return <PlaylistContext.Provider value={value}>{children}</PlaylistContext.Provider>;
}

export function usePlaylists() {
  const context = useContext(PlaylistContext);
  if (context === undefined) {
    throw new Error('usePlaylists must be used within PlaylistProvider');
  }
  return context;
}
